import React from 'react';
import {StyleSheet, TextStyle, TouchableOpacity, View, ViewStyle} from 'react-native';
import {NavigationState, useNavigation, useNavigationState} from '@react-navigation/native';
import {StackNavigationProp} from '@react-navigation/stack';

import Text from '../components/ui-controls/Text';
import {AppRoute} from '../models/AppRoute';
import {AppRoutesNavigatorParamList} from '../models/navigationTypes';
import theme from '../style/theme';
import {brandRewardsCopy} from '../utils/rewards';

type LinkProps = {
  children: React.ReactNode;
  target: keyof AppRoutesNavigatorParamList;
  isActive?: boolean;
};

const Link = ({children, target, isActive}: LinkProps) => {
  const navigation = useNavigation<StackNavigationProp<AppRoutesNavigatorParamList>>();

  const handlePress = () => {
    // Check if the target route is already in the stack
    const state = navigation.getState() as NavigationState | undefined;
    const routes = state?.routes || [];
    const isRouteInStack = routes.some(r => r.name === target);

    if (isRouteInStack) {
      // If it exists, pop back to it (unmounts screens on top)
      // This prevents duplicates in the stack
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (navigation as any).popTo(target);
    } else {
      // If it doesn't exist, push it to the stack
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (navigation as any).navigate(target);
    }
  };

  return (
    <TouchableOpacity
      onPress={handlePress}
      style={[styles.linkContainer, isActive && styles.linkContainerActive]}>
      <Text style={[styles.link, isActive && styles.linkActive]}>{children}</Text>
    </TouchableOpacity>
  );
};

type WebDesktopTopNavigationProps = {
  storeLoyaltyEnabled: boolean;
  ecommerceDisabled?: boolean;
};

const WebDesktopTopNavigation = ({
  ecommerceDisabled,
  storeLoyaltyEnabled,
}: WebDesktopTopNavigationProps) => {
  // Get current route name from navigation state
  const currentRouteName = useNavigationState(state => {
    if (!state?.routes?.length) return null;
    const currentRoute = state.routes[state.index];
    return currentRoute?.name;
  });

  const isActive = (route: keyof AppRoutesNavigatorParamList) => currentRouteName === route;

  return (
    <View style={styles.main}>
      <Link target={AppRoute.HOME} isActive={isActive(AppRoute.HOME)}>
        HOME
      </Link>
      {!ecommerceDisabled && (
        <Link target={AppRoute.SHOP} isActive={isActive(AppRoute.SHOP)}>
          SHOP
        </Link>
      )}
      <Link target={AppRoute.DISCOVER} isActive={isActive(AppRoute.DISCOVER)}>
        DISCOVER
      </Link>
      {storeLoyaltyEnabled ? (
        <Link target={AppRoute.REWARDS} isActive={isActive(AppRoute.REWARDS)}>
          {brandRewardsCopy.menuTitle.toUpperCase()}
        </Link>
      ) : !ecommerceDisabled ? (
        <Link target={AppRoute.WISHLIST} isActive={isActive(AppRoute.WISHLIST)}>
          WISHLIST
        </Link>
      ) : null}
      <Link target={AppRoute.CATALOGUE} isActive={isActive(AppRoute.CATALOGUE)}>
        CATALOGUES
      </Link>
      <Link target={AppRoute.PROMOTIONS} isActive={isActive(AppRoute.PROMOTIONS)}>
        PROMOTIONS
      </Link>
    </View>
  );
};

export default WebDesktopTopNavigation;

const styles = StyleSheet.create({
  main: {
    alignItems: 'center',
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'center',
    minHeight: 44,
    paddingVertical: 8,
  },
  linkContainer: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 4,
  } as ViewStyle,
  linkContainerActive: {
    backgroundColor: theme.colour.desktopNavTab?.activeBg,
    shadowColor: '#000',
    shadowOffset: {width: 0, height: 1},
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 2,
  } as ViewStyle,
  link: {
    color: theme.colour.desktopNavTab?.inactiveText ?? theme.colour.text.drawer.primary,
    fontWeight: '400',
  } as TextStyle,
  linkActive: {
    color: theme.colour.desktopNavTab?.activeText,
    fontWeight: '700',
  } as TextStyle,
});
